'use client';

import React, { useLayoutEffect, useRef } from 'react';
import HeroCoverReel from '@/components/comix/HeroCoverReel';
import PrimaryButton from '@/components/shared/PrimaryButton';
import { MediaBadgesStrip } from './MediaBadges';

type HeroSectionProps = {
  ctaLabel: string;
  onGetStarted: () => void;
  ctaRef: React.Ref<HTMLDivElement>;
};

// Matches PrimaryButton's existing `size="small"` numbers (h-[44px], px-4, text-[14px],
// 16px radius) — reusing the design system's own compact button rather than inventing
// new ones. Applied via direct style writes (see below) since `size` is a render-time
// prop on PrimaryButton, not something this effect can flip after the fact.
const COMPACT = {
  h1: { fontSize: '26px', lineHeight: '1.25' },
  p: { fontSize: '14px', lineHeight: '20px', marginTop: '8px' },
  ctaMarginTop: '12px',
  btn: { height: '44px', fontSize: '14px', paddingLeft: '16px', paddingRight: '16px', borderRadius: '16px' },
};

// Mirrors the JSX below exactly (h1/p's own inline `style` props, PrimaryButton's
// BTN_STYLE.borderRadius for the "regular" size) — these are hardcoded constants,
// not read off the DOM, on purpose: see the comment above `applyCompact` for why.
const NORMAL = {
  h1: { fontSize: 'var(--hp-h1)', lineHeight: '1.35' },
  p: { lineHeight: 'var(--hp-lede-lh)' },
  btn: { borderRadius: '20px' },
};

/**
 * Shrinks the hero's headline, subtitle, and CTA button if that's what it takes for
 * StoryBanner ("Multiple characters. Your storyline.", id="story-banner", rendered
 * right after this section — see comix/page.tsx) to start within the first viewport
 * height, so a user doesn't have to scroll to see it.
 *
 * Only ever triggered by a real `resize` event, never by a `ResizeObserver` on an
 * element this effect itself mutates — watching the thing you're changing can turn
 * into a feedback loop (hit exactly that while building the CTA-button fit fix
 * earlier). `fit()` always resets to normal before re-measuring, so every check is a
 * single, deterministic before/after read — never an iterative shrink loop.
 *
 * The button is grabbed via a ref (btnRef), not `document.getElementById` — this
 * component sits inside `<Suspense fallback={null}>` (see comix/page.tsx), which can
 * leave a second, detached copy of its subtree's DOM in place with the same ids;
 * `getElementById('btn-get-started-hero')` isn't guaranteed to return the one this
 * instance is actually rendering (caught this live: the inline style said one size,
 * getComputedStyle said another — this effect was silently styling the wrong node).
 */
function useCompactHeroIfBelowFold() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const ctaWrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const heading = headingRef.current;
    const paragraph = paragraphRef.current;
    const ctaWrap = ctaWrapRef.current;
    const btn = btnRef.current;
    if (!heading || !paragraph || !ctaWrap || !btn) return;

    // Some of these (heading font-size/line-height, the paragraph's line-height,
    // the button's border-radius) are only ever set via React's own inline `style`
    // prop, with no CSS class underneath them — blanking them to '' doesn't "restore
    // the original," it deletes the only declaration that had it, collapsing to
    // whatever inherits from further up (which is how this first shipped rendering
    // the h1 at 16px instead of 32px on every load, compact or not).
    //
    // The fix isn't "capture the DOM's current value before first touching it" —
    // tried that, and it breaks under React Strict Mode's dev-only double-invoke
    // (mount → cleanup → mount again): the cleanup here only removes the resize
    // listener, it doesn't undo style writes, so the *second* mount's "capture the
    // original" step read back the *first* mount's already-compacted value as if it
    // were the baseline, permanently pinning the heading compact. Using fixed
    // NORMAL constants (mirroring the JSX below) instead of a DOM read sidesteps
    // that entirely — there's nothing to poison.
    const applyCompact = (on: boolean) => {
      heading.style.fontSize = on ? COMPACT.h1.fontSize : NORMAL.h1.fontSize;
      heading.style.lineHeight = on ? COMPACT.h1.lineHeight : NORMAL.h1.lineHeight;
      // Font-size/margin-top on the paragraph and height/font-size/padding on the
      // button DO come from Tailwind classes (text-[18px], mt-3, h-[60px], px-6, ...),
      // so clearing those specific ones to '' correctly falls back to the class.
      paragraph.style.fontSize = on ? COMPACT.p.fontSize : '';
      paragraph.style.lineHeight = on ? COMPACT.p.lineHeight : NORMAL.p.lineHeight;
      paragraph.style.marginTop = on ? COMPACT.p.marginTop : '';
      ctaWrap.style.marginTop = on ? COMPACT.ctaMarginTop : '';
      btn.style.height = on ? COMPACT.btn.height : '';
      btn.style.fontSize = on ? COMPACT.btn.fontSize : '';
      btn.style.paddingLeft = on ? COMPACT.btn.paddingLeft : '';
      btn.style.paddingRight = on ? COMPACT.btn.paddingRight : '';
      btn.style.borderRadius = on ? COMPACT.btn.borderRadius : NORMAL.btn.borderRadius;
    };

    const fit = () => {
      const storyBanner = document.getElementById('story-banner');
      if (!storyBanner) return;
      applyCompact(false); // always re-measure from normal — never shrink an already-shrunk read
      // Require a 12px sliver of the next section to be on screen for the hero to count as
      // "fitting" — so it compacts slightly sooner and #story-banner peeks in a bit more often.
      const NEXT_SECTION_PEEK = 12;
      const fitsAboveTheFold =
        storyBanner.getBoundingClientRect().top <= window.innerHeight - NEXT_SECTION_PEEK;
      if (!fitsAboveTheFold) applyCompact(true);
    };

    fit();
    document.fonts?.ready?.then(fit).catch(() => {});

    // `resize` only fires on real viewport-dimension changes — it does NOT fire for
    // ordinary content reflow (an image locking in its final size, a web font
    // swapping in with different metrics, async content arriving). Caught this live:
    // the page kept settling for a couple of seconds after mount/fonts.ready with no
    // resize event at all, leaving a stale decision behind.
    //
    // Watch the `.hp` page wrapper, not `document.body` — tried body first and it
    // never fired: body is pinned to the viewport height (100dvh, see
    // src/app/layout.tsx), so it doesn't resize when content inside it grows or
    // shrinks. `.hp` (page.tsx's own wrapper, the nearest ancestor whose box is
    // actually driven by its content rather than the viewport) does.
    //
    // Safe to have this potentially notice `fit`'s own writes (compacting shrinks
    // `.hp`'s height too) because `fit` is single-pass and idempotent — it always
    // resets to normal and recomputes from scratch, never accumulates or shrinks
    // further on repeat calls — so even a self-triggered notification just
    // recomputes the same answer again; it can't drift or oscillate.
    const hpWrapper = heading.closest<HTMLElement>('.hp');
    const contentObserver = hpWrapper ? new ResizeObserver(fit) : null;
    if (hpWrapper && contentObserver) contentObserver.observe(hpWrapper);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fit, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      contentObserver?.disconnect();
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  return { headingRef, paragraphRef, ctaWrapRef, btnRef };
}

// ctaWrapRef (this file's own margin-top control) and the ctaRef prop (ComixHomeClient's
// sticky-CTA scroll target) both need to sit on the same div — React only takes one
// `ref` per element, so merge them into a single callback ref.
function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === 'function') ref(node);
      else (ref as React.RefObject<T | null>).current = node;
    }
  };
}

/**
 * Figma: desktop 303:3213 / mobile 303:2835.
 * The Figma hero art sits in a layer named "Video [Replace]" — it's a placeholder for
 * the existing hero video, so HeroVideo fills that box. Both breakpoints use the same
 * 527:479 media ratio, so a single aspect box serves them.
 */
export default function HeroSection({ ctaLabel, onGetStarted, ctaRef }: HeroSectionProps) {
  const { headingRef, paragraphRef, ctaWrapRef, btnRef } = useCompactHeroIfBelowFold();

  // White on mobile (bg-[var(--hp-hero-bg)]); desktop-only override to #F6F0F0.
  return (
    <section className="w-full bg-[var(--hp-hero-bg)] dt:bg-[#F6F0F0]">
      {/* --hero: 1.5vw gutter on mobile only; desktop keeps the shared --hp-gutter. */}
      <div className="hp-container hp-container--hero">
        {/* Mobile stacks video-above-copy; desktop puts copy left, video right.
            Column widths are Figma's 383.22 / 527.40 of the 1040 container, so the gap
            between them stays proportional as the container scales down. Mobile has no
            top padding and no gap — the video (first, via flex-col-reverse) sits flush
            under the header with the copy immediately below it; bottom padding unchanged. */}
        <div className="flex flex-col-reverse items-center gap-0 pt-0 pb-[32px] dt:flex-row dt:items-center dt:justify-between dt:gap-0 dt:py-[2.83%]">
          {/* dt:-translate-y-1 (4px): nudges the headline/subtitle/CTA up on desktop only. */}
          <div className="flex w-full flex-col items-center text-center dt:w-[36.85%] dt:items-start dt:text-left dt:-translate-y-1">
            <h1
              ref={headingRef}
              className="font-bold tracking-[-0.454px] text-[var(--hp-ink)]"
              style={{ fontSize: 'var(--hp-h1)', lineHeight: 1.35 }}
            >
              {/* Explicit {' '} before the <br/> — JSX text nodes don't carry a space
                  across a line break, so without it the accessible name/extracted text
                  reads "Make your owncomic adventure" (confirmed by a content audit).
                  No visual change: browsers collapse trailing whitespace at a line break. */}
              Make your own{' '}
              <br />
              comic adventure
            </h1>

            {/* 18px / 30px line-height / #000 at 80% opacity, per Figma — matches the
                mobile --hp-lede value, but hardcoded here rather than reusing the shared
                var, since that var is also used by the footer's "Need help?" copy. */}
            <p
              ref={paragraphRef}
              className="mt-3 max-w-[320px] text-[18px] text-[var(--hp-lede-color)] dt:mt-3 dt:max-w-none dt:text-[length:var(--hp-lede)]"
              style={{ lineHeight: 'var(--hp-lede-lh)' }}
            >
              {/* Forces "your favorite people" onto its own line on both breakpoints —
                  natural wrapping was inconsistent about where "your" landed. */}
              A custom printed book starring
              <br /> your favorite people
            </p>

            {/* Figma: 36px above the CTA. */}
            <div ref={mergeRefs(ctaRef, ctaWrapRef)} className="mt-[24px] dt:mt-[36px]">
              <PrimaryButton
                ref={btnRef}
                id="btn-get-started-hero"
                onClick={onGetStarted}
                className="h-[60px] w-[200px] rounded-[20px]"
              >
                {ctaLabel}
              </PrimaryButton>
            </div>

            {/* Desktop only — mobile shows the same strip as its own section below the
                story banner. Figma: 56px between the CTA and the badges. */}
            <div className="hidden w-full dt:mt-14 dt:block">
              {/* Stars only — the badges themselves are there from the first paint. */}
              <MediaBadgesStrip animateStars className="dt:mx-0 dt:justify-start" />
            </div>
          </div>

          <div className="w-full dt:w-[50.71%]">
            {/* Same box the hero video used: 527:479, full width of the column, so the artwork
                scales with the viewport exactly as before. The reel's own artboard is 393:357
                (the videos were this same mockup at 2x/2.5x), so it fills the box edge to edge.
                It has no background of its own — the section colour shows through. */}
            <div className="hp-hero-media relative aspect-[527/479] w-full overflow-hidden">
              <HeroCoverReel className="absolute inset-0 h-full w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
